import classnames from 'classnames/bind';
import React from 'react';

import APIError from 'types/api_error';

import { useAPI } from 'hooks/api/useAPI';
import { isObject, objectHas } from 'services/object';
import { connectWhep, WhepConnection } from 'services/whep';

import { Text } from 'components/Text/Text';

import { BasePlayerProps, WebRTCPlayerMode } from './types';
import styles from './WebRTCPlayer.module.scss';

const c = classnames.bind(styles);

function hasWhepUnsupportedCode(value: unknown): boolean {
  if (typeof value === 'object' && value !== null && 'code' in value) {
    return (value as { code: unknown }).code === 'whep-unsupported';
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed?.code === 'whep-unsupported';
    } catch {
      return false;
    }
  }
  return false;
}

function isWhepUnsupportedError(error: unknown): boolean {
  // Direct API error object or JSON string from interceptor
  if (hasWhepUnsupportedCode(error)) return true;
  // AxiosError that wasn't fully unwrapped — check nested response.data
  if (isObject(error) && objectHas(error, 'response')) {
    const resp = (error as { response: unknown }).response;
    if (isObject(resp) && objectHas(resp, 'data')) {
      return hasWhepUnsupportedCode((resp as { data: unknown }).data);
    }
  }
  return false;
}

type WebRTCPlayerWhepProps = BasePlayerProps & {
  onWhepUnsupported?: () => void;
};

export function WebRTCPlayerWhep({
  className,
  streamID,
  auto,
  onWhepUnsupported,
}: WebRTCPlayerWhepProps) {
  const api = useAPI();
  const [mode, setMode] = React.useState<WebRTCPlayerMode>('loading');
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const [attempts, setAttempts] = React.useState(1);

  const videoRef = React.useRef<HTMLVideoElement>(null);
  const whepConnection = React.useRef<WhepConnection>();
  const shouldUseFallbackProfileLevel = React.useRef(false);

  React.useEffect(() => {
    const abortController = new AbortController();

    setMode('loading');

    function showErrorState(error: APIError | unknown) {
      if (abortController.signal.aborted) return;

      if (
        error instanceof Error &&
        (error.name === 'AbortError' || error.name === 'CanceledError')
      ) {
        return;
      }

      if (isWhepUnsupportedError(error)) {
        onWhepUnsupported?.();
        return;
      }

      setMode('error');

      if (
        error instanceof Error &&
        (error.name === 'OperationError' ||
          error.name === 'InvalidAccessError' ||
          error.name === 'RTCError')
      ) {
        shouldUseFallbackProfileLevel.current = true;
      }

      if (isObject(error) && objectHas(error, 'message')) {
        setErrorMessage(
          `${(error as { message: string }).message}. Retrying in 2 seconds..`
        );
      } else {
        setErrorMessage(`${String(error)}. Retrying in 2 seconds..`);
      }

      window.setTimeout(() => {
        setAttempts((prev) => prev + 1);
      }, 2000);
    }

    const iceServers: RTCIceServer[] = [
      { urls: 'stun:traverse.lumeo.com:3478' },
      {
        urls: 'turns:traverse.lumeo.com',
        username: 'lumeo',
        credential: 'video',
      },
    ];

    connectWhep(
      (offerSdp) => {
        if (abortController.signal.aborted) {
          return Promise.reject(new Error('Connection cancelled'));
        }
        return api.webrtc.whepConnect(
          streamID,
          offerSdp,
          abortController.signal
        );
      },
      (sessionId, sdpfrag) => {
        if (abortController.signal.aborted) {
          return Promise.reject(new Error('Connection cancelled'));
        }
        return api.webrtc.whepPatch(streamID, sessionId, sdpfrag);
      },
      {
        iceServers,
        onTrack: (event) => {
          if (abortController.signal.aborted) return;

          const receiver = event.receiver;
          if (receiver && 'jitterBufferTarget' in receiver) {
            (receiver as RTCRtpReceiver & { jitterBufferTarget: number }).jitterBufferTarget = 100;
          }

          const stream = event.streams[0];
          if (videoRef.current && stream) {
            videoRef.current.srcObject = stream;
            videoRef.current.play().catch(() => {
              // Autoplay may be blocked by browser policy
            });
          }
        },
        onDisconnect: (sessionId) =>
          api.webrtc.whepDisconnect(streamID, sessionId),
      }
    )
      .then((conn) => {
        if (abortController.signal.aborted) {
          conn.close();
          return;
        }
        whepConnection.current = conn;
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        showErrorState(error);
      });

    return () => {
      abortController.abort();
      if (whepConnection.current) {
        whepConnection.current.close();
        whepConnection.current = undefined;
      }
    };
  }, [attempts, streamID, api.webrtc, onWhepUnsupported]);

  React.useEffect(() => {
    const loadedCheck = window.setInterval(() => {
      if (videoRef.current) {
        const video = videoRef.current;
        if (video.readyState >= 2) {
          setMode('idle');
        }
      }
    }, 1000);

    return () => {
      window.clearInterval(loadedCheck);
    };
  }, [attempts]);

  return (
    <div id={streamID} className={c('wrap', className, mode, { auto })}>
      {mode === 'loading' && (
        <div className={c('loader')}>
          <div className="loading-spinner" />
        </div>
      )}

      <video ref={videoRef} className={c('video')} muted controls autoPlay />

      {mode === 'error' && (
        <div className={c('error-message')}>
          <p>
            Stream could not be loaded due to network issues or incompatible
            format.
            {errorMessage && (
              <Text inline>
                <br />
                {errorMessage}
              </Text>
            )}
          </p>
        </div>
      )}
    </div>
  );
}
